import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import {
  getTranslatedGenreList,
  getTranslatedCategoryList,
  toggleCategorySelection,
  canSubmitGenreCategory,
  MAX_CATEGORY_COUNT,
} from "@/lib/utils/novelSettingsHelper";
import { Novel } from "@/lib/models";
import IconClose from "~icons/figma/Close";
import IconChevronDown from "~icons/figma/ChevronDown";


export default /*#__PURE__*/_defineComponent({
  __name: 'GenreCategorySettingDialog',
  props: {
    novelId: { type: String, required: true }
  },
  setup(__props: any) {

const props = __props as {
  novelId: string;
};

/* eslint-disable no-undef -- defineProps is a Vue compiler macro */


const titleId = "genre-category-setting-title";
const categorySelectLabelId = "genre-category-setting-category-select-label";

const instance = getCurrentInstance();
const proxy = instance?.proxy;
const store = proxy?.$store;

const translatedGenreList = proxy ? getTranslatedGenreList(proxy.$t.bind(proxy)) : [];
const translatedCategoryList = proxy ? getTranslatedCategoryList(proxy.$t.bind(proxy)) : [];

const novel = store?.getters["novelModule/novel"](props.novelId) as Novel | undefined;

// 廃止・不正な選択肢IDを持つレガシーデータの場合、選択肢一覧に存在しないIDをそのまま保持すると
// セレクトが空欄のままcanSubmitがtrueになったり、見えないIDが上限枠を消費してしまうため、
// 初期化時に選択肢一覧に存在するIDのみを残す
const validGenreIds = new Set(translatedGenreList.map((item) => item.id));
const validCategoryIds = new Set(translatedCategoryList.map((item) => item.id));

const genreId = ref<string | undefined>(novel?.genre && validGenreIds.has(novel.genre) ? novel.genre : undefined);
const categoryIds = ref<string[]>((novel?.category ?? []).filter((id) => validCategoryIds.has(id)));
const isSaving = ref(false);
const dialogRoot = ref<HTMLElement | null>(null);

// ジャンル・カテゴリはともに必須（未選択のまま保存できてしまうと、作品作成時のバリデーションと
// 矛盾するデータ状態を作ってしまうため）
const canSubmit = computed(() => canSubmitGenreCategory(genreId.value, categoryIds.value));

function onClickCancel() {
  if (isSaving.value) return;
  proxy?.$close(false);
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function getFocusableElements(): HTMLElement[] {
  if (!dialogRoot.value) return [];
  return Array.from(dialogRoot.value.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
}

// Escapeで閉じ、Tabがダイアログの外(背後のページ)へ抜けないようループさせる
function onKeydown(e: KeyboardEvent) {
  if (e.key === "Escape") {
    onClickCancel();
    return;
  }
  if (e.key !== "Tab") return;

  const focusable = getFocusableElements();
  if (focusable.length === 0) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  // 開いた直後はfirst/lastどちらでもないダイアログ本体(tabindex="-1")にフォーカスがあるため、
  // activeElementがfirst/lastの場合だけでなく、ダイアログ本体そのものの場合も起点として扱う
  // (これを入れないと、開いた直後の1回目のShift+Tabだけダイアログの外(背後のページ)へ抜けてしまう)
  const atStart = document.activeElement === first || document.activeElement === dialogRoot.value;
  const atEnd = document.activeElement === last || document.activeElement === dialogRoot.value;

  if (e.shiftKey && atStart) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && atEnd) {
    e.preventDefault();
    first.focus();
  }
}

onMounted(() => {
  nextTick(() => dialogRoot.value?.focus());
  document.addEventListener("keydown", onKeydown);
});
onBeforeUnmount(() => document.removeEventListener("keydown", onKeydown));

function onChangeGenre(event: Event) {
  const target = event.target as HTMLSelectElement;
  genreId.value = target.value || undefined;
}

function onToggleCategory(id: string) {
  categoryIds.value = toggleCategorySelection(categoryIds.value, id, MAX_CATEGORY_COUNT);
}

async function onClickUpdate() {
  if (!canSubmit.value) {
    return;
  }

  isSaving.value = true;
  try {
    if (!store?.getters["novelModule/novel"](props.novelId)) {
      // 作品がストアから消えている場合は何度リトライしても保存できないため、
      // 通知したうえでダイアログを閉じる（開いたままだとクリックの度にトーストが増え続ける）
      proxy?.$notify({
        title: proxy?.$t("novelDetail.genreCategorySetting.saveFailed"),
        type: "error",
      });
      proxy?.$close(false);
      return;
    }

    const updated = await store?.dispatch("novelModule/updateNovel", {
      novelId: props.novelId,
      genre: genreId.value,
      category: categoryIds.value,
    });
    if (!updated) {
      // GraphQLがエラーを含む場合等、実際には保存されていないため成功扱いにしない
      // （エラー内容はGraphQLClient側の共通ダイアログで既に表示されている）
      return;
    }

    proxy?.$notify({
      title: proxy?.$t("common.saved"),
      type: "success",
      duration: 2000,
    });
    proxy?.$close(true);
  } catch (error) {
    // 通信エラー等は updateNovelの呼び出し元であるGraphQLClient側で
    // 既にエラーダイアログの表示・ログ送信が行われているため、ここでは重複させない
  } finally {
    isSaving.value = false;
  }
}

return { __sfc: true,props, titleId, categorySelectLabelId, instance, proxy, store, translatedGenreList, translatedCategoryList, novel, validGenreIds, validCategoryIds, genreId, categoryIds, isSaving, dialogRoot, canSubmit, onClickCancel, FOCUSABLE_SELECTOR, getFocusableElements, onKeydown, onChangeGenre, onToggleCategory, onClickUpdate, MAX_CATEGORY_COUNT, IconClose, IconChevronDown }
}

})